<template>
  <FlashNotifications ref="flash" />

  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

    <!-- ═══════════ Card header ═══════════ -->
    <div class="px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
      <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
        <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <rect x="5" y="2" width="14" height="20" rx="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path stroke-linecap="round" stroke-linejoin="round" d="M11 18h2"/>
        </svg>
      </div>
      <div class="min-w-0 flex-1">
        <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ t.title }}</h2>
        <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none truncate">{{ t.subtitle }}</p>
      </div>
      <span v-if="!loading && status"
            class="flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold"
            :class="status.enabled
              ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'">
        {{ status.enabled ? t.status_on : t.status_off }}
      </span>
    </div>

    <!-- ═══════════ Loading skeleton ═══════════ -->
    <div v-if="loading" class="px-5 sm:px-6 py-5 animate-pulse space-y-3">
      <div class="h-2.5 w-full bg-gray-100 dark:bg-gray-800 rounded"></div>
      <div class="h-2.5 w-3/4 bg-gray-100 dark:bg-gray-800 rounded"></div>
      <div class="pt-2">
        <div class="h-10 w-56 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
    </div>

    <!-- ═══════════ Error ═══════════ -->
    <div v-else-if="storeError" class="flex flex-col items-center justify-center py-16 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ storeError }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">
        Try again
      </button>
    </div>

    <template v-else>

      <!-- ═══════════ Backup codes, shown once ═══════════ -->
      <div v-if="backupCodes.length" class="px-5 sm:px-6 py-5">
        <div class="flex items-start gap-2.5 mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
          <svg class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.008v.008H12v-.008Z"/>
          </svg>
          <div>
            <p class="text-xs font-semibold text-amber-800 dark:text-amber-300 leading-snug">{{ t.backup_title }}</p>
            <p class="text-xs text-amber-700 dark:text-amber-400 mt-1 leading-relaxed">{{ t.backup_body }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 mb-4">
          <code v-for="code in backupCodes" :key="code"
                class="px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-gray-700 text-sm font-mono tracking-wider text-gray-800 dark:text-gray-200 text-center select-all">
            {{ code }}
          </code>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <button @click="copyBackupCodes"
                  class="px-4 py-2 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-semibold rounded-lg transition-colors">
            {{ t.backup_copy }}
          </button>
          <button @click="downloadBackupCodes"
                  class="px-4 py-2 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-semibold rounded-lg transition-colors">
            {{ t.backup_download }}
          </button>
          <button @click="backupCodes = []"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
            {{ t.backup_done }}
          </button>
        </div>
      </div>

      <!-- ═══════════ Setup in progress ═══════════ -->
      <div v-else-if="setup" class="px-5 sm:px-6 py-5">
        <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">{{ t.step_scan_title }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">{{ t.step_scan_body }}</p>

        <div class="flex flex-col sm:flex-row sm:items-start gap-5">
          <div class="flex-shrink-0 self-center sm:self-start p-3 bg-white rounded-xl border border-gray-100 dark:border-gray-700">
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="" width="180" height="180" class="block w-[180px] h-[180px]">
            <div v-else class="w-[180px] h-[180px] bg-gray-100 rounded animate-pulse"></div>
          </div>

          <div class="flex-1 min-w-0">
            <label class="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
              {{ t.manual_entry_label }}
            </label>
            <button type="button" @click="copyManualKey"
                    class="w-full text-left px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-gray-700 text-xs font-mono tracking-wider text-gray-700 dark:text-gray-300 break-all hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
              {{ setup.manual_entry_key }}
            </button>

            <p class="text-sm font-semibold text-gray-900 dark:text-white mt-5 mb-1">{{ t.step_verify_title }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">{{ t.step_verify_body }}</p>

            <form @submit.prevent="confirmSetup" class="space-y-3">
              <input v-model="otpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="6"
                     placeholder="000000" spellcheck="false"
                     class="w-full sm:w-44 px-3.5 py-2.5 text-lg font-semibold tracking-[0.35em] text-center border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">

              <p v-if="formError" class="flex items-center gap-1 text-xs text-red-500">
                <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
                {{ formError }}
              </p>

              <div class="flex items-center gap-2">
                <button type="submit" :disabled="saving || otpCode.length < 6"
                        class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
                  <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  {{ t.verify_cta }}
                </button>
                <button type="button" @click="cancelSetup" :disabled="saving"
                        class="px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-semibold transition-colors">
                  {{ t.cancel }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- ═══════════ Enabled ═══════════ -->
      <div v-else-if="status.enabled" class="px-5 sm:px-6 py-5">
        <div class="flex items-start gap-2.5 mb-4">
          <div class="w-5 h-5 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg class="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
          </div>
          <div>
            <p class="text-sm text-gray-700 dark:text-gray-200 leading-snug">{{ enabledSinceLabel }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ backupRemainingLabel }}</p>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <button @click="openConfirm('regenerate')"
                  class="px-4 py-2 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-semibold rounded-lg transition-colors">
            {{ t.regenerate_cta }}
          </button>
          <button @click="openConfirm('disable')"
                  class="px-4 py-2 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-semibold rounded-lg transition-colors">
            {{ t.disable_cta }}
          </button>
        </div>
      </div>

      <!-- ═══════════ Disabled ═══════════ -->
      <div v-else class="px-5 sm:px-6 py-5">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed max-w-2xl">{{ t.off_body }}</p>
        <button @click="beginSetup" :disabled="saving"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
          <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
          {{ t.enable_cta }}
        </button>
      </div>

    </template>
  </div>

  <!-- ═══════════ Confirm dialog ═══════════ -->
  <div v-if="confirmAction" class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50" @click.self="closeConfirm">
    <div class="w-full max-w-sm bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
          {{ confirmAction === 'disable' ? t.confirm_disable_title : t.confirm_regenerate_title }}
        </h3>
      </div>

      <form @submit.prevent="submitConfirm" class="px-5 py-4 space-y-3">
        <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          {{ confirmAction === 'disable' ? t.confirm_disable_body : t.regenerate_warning }}
        </p>

        <div>
          <label class="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ usePasswordToConfirm ? t.confirm_password_label : t.confirm_code_label }}
          </label>
          <input v-model="confirmValue"
                 :type="usePasswordToConfirm ? 'password' : 'text'"
                 :inputmode="usePasswordToConfirm ? undefined : 'numeric'"
                 :autocomplete="usePasswordToConfirm ? 'current-password' : 'one-time-code'"
                 spellcheck="false"
                 class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
        </div>

        <p v-if="formError" class="flex items-center gap-1 text-xs text-red-500">
          <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
          {{ formError }}
        </p>

        <div class="flex items-center justify-end gap-2 pt-1">
          <button type="button" @click="closeConfirm" :disabled="saving"
                  class="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-semibold transition-colors">
            {{ t.cancel }}
          </button>
          <button type="submit" :disabled="saving || !confirmValue"
                  class="inline-flex items-center gap-2 px-5 py-2 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="confirmAction === 'disable' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'">
            <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {{ t.confirm }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import QRCode from 'qrcode'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import { useTwoFactorStore } from 'stores/two_factor.js'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N, parseLocale } = useConstants()
const store = useTwoFactorStore()

const t = I18N.two_factor.settings

const saving = ref(false)
const formError = ref(null)
const setup = ref(null)
const qrDataUrl = ref(null)
const otpCode = ref('')
const backupCodes = ref([])
const confirmAction = ref(null)
const confirmValue = ref('')

const status = computed(() => store.status)
const loading = computed(() => store.loading || (!store.status && !store.error))
const storeError = computed(() => store.error)

// Google-only accounts have no password the user ever chose, so a live code is
// the only thing they can confirm a downgrade with.
const usePasswordToConfirm = computed(() => store.status?.requires_password !== false)

const enabledSinceLabel = computed(() => {
  if (!store.status?.enabled_at) return t.status_on
  const date = new Date(store.status.enabled_at).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
  return parseLocale(t.enabled_since, { date })
})

const backupRemainingLabel = computed(() =>
  parseLocale(t.backup_remaining, { count: store.status?.backup_codes_remaining ?? 0 })
)

onMounted(() => store.fetchIfStale())

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
}

// redaxios rejects with the response object on a non 2xx, so the server's own
// message is one level deeper than on the success path.
function messageFrom(res, fallback) {
  return res?.response?.message || res?.error?.data?.message || fallback
}

async function beginSetup() {
  saving.value = true
  formError.value = null
  const res = await axiosRequest('POST', '/api/v1/two_factor', {}, false)
  saving.value = false

  if (res.js_error || !res.response?.success) {
    flash.value?.show(messageFrom(res, t.generic_error), 'alert')
    return
  }

  setup.value = res.response.data
  otpCode.value = ''
  qrDataUrl.value = await QRCode.toDataURL(setup.value.provisioning_uri, { width: 360, margin: 1 })
}

function cancelSetup() {
  setup.value = null
  qrDataUrl.value = null
  otpCode.value = ''
  formError.value = null
}

async function confirmSetup() {
  saving.value = true
  formError.value = null
  const res = await axiosRequest('PUT', '/api/v1/two_factor', { otp_code: otpCode.value }, false)
  saving.value = false

  if (res.js_error || !res.response?.success) {
    formError.value = messageFrom(res, t.generic_error)
    return
  }

  const data = res.response.data
  store.setStatus(statusFrom(data))
  backupCodes.value = data.backup_codes
  cancelSetup()
  flash.value?.show(res.response.message, 'notice')
}

function openConfirm(action) {
  confirmAction.value = action
  confirmValue.value = ''
  formError.value = null
}

function closeConfirm() {
  confirmAction.value = null
  confirmValue.value = ''
  formError.value = null
}

async function submitConfirm() {
  const payload = usePasswordToConfirm.value
    ? { password: confirmValue.value }
    : { otp_code: confirmValue.value }

  saving.value = true
  formError.value = null

  const res = confirmAction.value === 'disable'
    ? await axiosRequest('DELETE', '/api/v1/two_factor', payload, false)
    : await axiosRequest('POST', '/api/v1/two_factor/backup_codes', payload, false)

  saving.value = false

  if (res.js_error || !res.response?.success) {
    formError.value = messageFrom(res, t.generic_error)
    return
  }

  const data = res.response.data
  store.setStatus(statusFrom(data))
  backupCodes.value = data.backup_codes || []
  const message = res.response.message
  closeConfirm()
  flash.value?.show(message, 'notice')
}

function statusFrom(data) {
  return {
    enabled: data.enabled,
    pending: data.pending,
    enabled_at: data.enabled_at,
    backup_codes_remaining: data.backup_codes_remaining,
    requires_password: data.requires_password,
  }
}

async function copyManualKey() {
  await navigator.clipboard.writeText(setup.value.manual_entry_key.replace(/\s/g, ''))
  flash.value?.show(t.manual_copied, 'notice')
}

async function copyBackupCodes() {
  await navigator.clipboard.writeText(backupCodes.value.join('\n'))
  flash.value?.show(t.backup_copied, 'notice')
}

function downloadBackupCodes() {
  const body = [t.backup_title, '', ...backupCodes.value].join('\n')
  const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }))
  const link = document.createElement('a')
  link.href = url
  link.download = 'timelydo-backup-codes.txt'
  link.click()
  URL.revokeObjectURL(url)
}
</script>
